import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  fetchGfDeal,
  postGfAdvance,
  postGfCancel,
  postGfContacted,
  postGfSetStage,
  postGfShipInstruction,
} from "../../../api/giftingClient";
import {
  CAMPAIGN_TYPE_LABEL,
  GF_PHASE,
  REUSE_STATE_LABEL,
  shipErrorText,
  STAGE_NAMES,
  entryAction,
  fmtMD,
  fmtYen,
  fmtYmd,
  handleOf,
  autoDmMessage,
  entryProductLabel,
  isArchived,
  linkedIgOf,
  mediaChip,
  nextLabel,
  primarySns,
  resolveRate,
  reuseScopeLabels,
  reuseState,
  typeHasAffiliate,
  typeHasFee,
  willAutoSendReuseDm,
  PLATFORM_LABEL,
  affiliateByInfluencer,
  type EntryAction,
} from "../../../lib/giftingRules";
import type {
  GfAdvanceInput,
  GfDealDetail,
  GfEntryPanelProps,
  GfPost,
  GfStage,
  Loadable,
} from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { AdvanceConfirm } from "../modals/AdvanceConfirm";
import { ConfirmModal } from "../modals/ConfirmModal";
import { GfAvatar, GfButton, GfErrorBanner, GfFacts, GfField, GfPanelFrame, GfSkeleton, GfTag, copyText } from "../ui/GfUi";
import { EntryTimeline } from "./entry/EntryTimeline";
import { FormUrlBox } from "./entry/FormUrlBox";
import { LinksSection } from "./entry/LinksSection";
import { DmPanel } from "./entry/DmPanel";
import { NotesSection } from "./entry/NotesSection";
import { PaymentSection } from "./entry/PaymentSection";
import { PostsSection } from "./entry/PostsSection";
import { TRACKING_CARRIER_ID, TrackingForm } from "./entry/TrackingForm";

type ModalKind = null | "adv3" | "adv6" | "adv7" | "cancel" | "stage" | "message" | "ship";

function reuseSummary(posts: GfPost[], nowIso: string): string {
  const states = posts.map((p) => ({ p, s: reuseState(p, nowIso) }));
  const g = states.find((x) => x.s === "granted" || x.s === "expiring");
  if (g) {
    const scopes = reuseScopeLabels(g.p.reuse_scope).join("・");
    const until = g.p.reuse_until ? `／〜${fmtYmd(g.p.reuse_until)}` : "";
    return `${REUSE_STATE_LABEL[g.s]}（${scopes || "用途未設定"}${until}）`;
  }
  if (states.some((x) => x.s === "requested")) return REUSE_STATE_LABEL.requested;
  if (states.some((x) => x.s === "expired")) return REUSE_STATE_LABEL.expired;
  return REUSE_STATE_LABEL.none;
}

function fmtPostal(s: string | null): string {
  const d = (s ?? "").replace(/\D/g, "");
  return d.length === 7 ? `〒${d.slice(0, 3)}-${d.slice(3)}` : s ? `〒${s}` : "";
}

/** 案件エントリー詳細パネル（案件・発送・ダッシュボードのタブで開く右サイドパネル） */
export function EntryPanel({ dealId, data, nowIso, nav, refresh, toast }: GfEntryPanelProps) {
  const entry = data.board.entries.find((e) => e.id === dealId) ?? null;
  const creator = entry ? data.board.creators.find((c) => c.id === entry.influencer_id) ?? null : null;
  const campaign = entry?.campaign_id ? data.board.campaigns.find((c) => c.id === entry.campaign_id) ?? null : null;

  const [detail, setDetail] = useState<Loadable<GfDealDetail>>({ status: "loading" });
  const [modal, setModal] = useState<ModalKind>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAddr, setShowAddr] = useState(false);
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [postsSignal, setPostsSignal] = useState(0);
  const [targetStage, setTargetStage] = useState<GfStage>(1);
  const trackInit = useRef(false);

  // ---- 詳細（PII を含む）の取得。住所などはここでだけ読む ----
  const loadDetail = useCallback(async () => {
    try {
      const d = await fetchGfDeal(dealId);
      setDetail({ status: "ok", data: d });
    } catch (e) {
      setDetail((prev) => (prev.status === "ok" ? prev : { status: "error", message: errMsg(e) }));
    }
  }, [dealId]);
  useEffect(() => {
    setDetail({ status: "loading" });
    trackInit.current = false;
    void loadDetail();
  }, [loadDetail]);

  // 追跡入力欄の初期値（詳細の初回読込時だけ）
  useEffect(() => {
    if (detail.status !== "ok" || trackInit.current) return;
    trackInit.current = true;
    setCarrier(detail.data.ship_carrier ?? "");
    setTrackingNo(detail.data.ship_tracking_no ?? "");
  }, [detail]);

  const changed = useCallback(async () => {
    await Promise.all([refresh(), loadDetail()]);
  }, [refresh, loadDetail]);

  // ---- Escape: モーダルを閉じるだけのときはパネルを閉じない（capture で押下時点の状態を記録） ----
  const escHadModal = useRef(false);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") escHadModal.current = !!document.querySelector(".gf-modal-overlay");
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, []);
  const closePanel = nav.closePanel;
  const onClose = useCallback(() => {
    if (escHadModal.current) {
      escHadModal.current = false;
      return;
    }
    closePanel();
  }, [closePanel]);

  if (!entry) {
    return (
      <GfPanelFrame onClose={onClose} label="エントリー詳細">
        <div className="gf-empty">このエントリーは見つかりませんでした（アーカイブ済み、または削除された可能性があります）。</div>
      </GfPanelFrame>
    );
  }

  const d = detail.status === "ok" ? detail.data : null;
  const archived = isArchived(entry);
  const cancelled = entry.status === "cancelled";
  const action: EntryAction | null = entryAction(entry, campaign, nowIso);
  const name = creator?.name ?? d?.influencer_name ?? "（不明）";
  const sns = creator ? [...creator.sns].sort((a, b) => (b.follower_count ?? -1) - (a.follower_count ?? -1)) : [];

  // ---- 料率・条件 ----
  let rateText = "—";
  let rateSrc = "";
  if (campaign && typeHasAffiliate(campaign.type)) {
    if (data.affiliates.status === "loading") rateText = "読込中…";
    else if (data.affiliates.status === "error") rateText = "取得できません";
    else {
      const r = resolveRate(affiliateByInfluencer(data.affiliates.data).get(entry.influencer_id), campaign);
      rateText = r.label;
      rateSrc = r.src === "個別" || r.src === "案件" ? `（${r.src}）` : "";
    }
  }
  const paid = entry.deal_type === "paid" || (!!campaign && typeHasFee(campaign.type));
  const feeText = paid ? fmtYen(entry.fee_amount ?? campaign?.fee_amount ?? 0) : "—";
  const typeText = campaign ? CAMPAIGN_TYPE_LABEL[campaign.type] : entry.deal_type === "paid" ? "有償" : "無償";

  // ---- 事実リスト ----
  const addrText =
    entry.address_state === "received"
      ? `入力済（${entry.address_source === "proxy" ? "代理入力" : "本人入力"}）`
      : entry.address_state === "form_sent"
        ? "フォーム送信済・未入力"
        : "—";
  const posts = d?.posts ?? null;
  const latest = entry.latest_post;
  const postText = latest
    ? `${PLATFORM_LABEL[latest.platform]}${latest.post_type ? ` ${latest.post_type}` : ""}${latest.posted_at ? ` ${fmtMD(latest.posted_at)}` : ""}${entry.post_count > 1 ? `（${entry.post_count}件）` : ""}`
    : "—";
  const reuseText = posts
    ? reuseSummary(posts, nowIso)
    : entry.reuse_allowed_count > 0
      ? `許諾済（${entry.reuse_allowed_count}件）`
      : "未取得";
  const factRows: Array<{ k: ReactNode; v: ReactNode; mono?: boolean }> = [
    {
      k: "住所",
      v: (
        <>
          {addrText}
          {entry.address_state === "received" && d && (
            <>
              {" "}
              <button type="button" className="gf-link-btn" onClick={() => setShowAddr((v) => !v)}>
                {showAddr ? "隠す" : "表示"}
              </button>
            </>
          )}
        </>
      ),
    },
    {
      k: "受注番号",
      v:
        entry.ship_order_no ??
        (entry.stage === 4 && entry.status === "address_received" && GF_PHASE >= 2 ? "出荷指示を送ると自動で採番" : "—"),
      mono: !!entry.ship_order_no,
    },
    {
      k: "追跡番号",
      v: entry.ship_tracking_no ? `${entry.ship_carrier ?? ""} ${entry.ship_tracking_no}`.trim() : "—",
      mono: !!entry.ship_tracking_no,
    },
    { k: "投稿", v: postText },
    { k: "2次利用", v: reuseText },
  ];
  if (entry.post_deadline) factRows.push({ k: "投稿期限", v: fmtYmd(entry.post_deadline) || entry.post_deadline, mono: true });
  // 送る商品（choice 案件はクリエイターが選んだセット。未選択は「選択待ち」）
  factRows.unshift({
    k: "送る商品",
    v: entry.product_pending ? (
      <>
        <GfTag tone="caution">選択待ち</GfTag> クリエイターが本人フォームで選びます
      </>
    ) : (
      entryProductLabel(entry, campaign)
    ),
  });
  if (GF_PHASE >= 2 && entry.stage === 4 && entry.status === "address_received" && !entry.ship_order_no) {
    factRows.splice(1, 0, {
      k: "出荷チェック",
      v: !entry.ship_validation ? "未チェック（送信時に確認）" : entry.ship_validation.ok ? "OK" : shipErrorText(entry.ship_validation.errors),
    });
  }

  // 出荷に使う SKU（choice 案件はクリエイターが選んだもの＝entry.sku のみ。fixed は案件の SKU も既定値）
  const shipSku = entry.sku ?? (campaign?.product_mode === "choice" ? null : campaign?.sku ?? null);

  // ---- 主ボタン ----
  const received = entry.stage === 4 && entry.status === "address_received";
  let mainLabel = "";
  let mainDisabled = false;
  if (archived) {
    mainLabel = nextLabel(entry, campaign, nowIso) || "完了";
    mainDisabled = true;
  } else if (entry.stage === 2) {
    mainLabel = action ? action.cta : "返信あり・条件を確定へ";
  } else if (entry.stage === 5) {
    mainLabel = entry.post_count >= 1 ? "投稿確認へ進める" : action?.cta ?? "投稿を記録する";
  } else if (action) {
    mainLabel = action.cta;
  } else {
    mainLabel = nextLabel(entry, campaign, nowIso);
    mainDisabled = true;
  }

  const scrollToPosts = (msg: string) => {
    toast(msg);
    setPostsSignal((n) => n + 1);
  };

  const doAdvance = async (input: GfAdvanceInput = {}): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await postGfAdvance(dealId, input);
      await changed();
      const to = r.entry.stage;
      const advanced = to >= 8 ? "アーカイブしました" : `「${STAGE_NAMES[to]}」へ進めました`;
      // 第3弾: 段階6→7 の2次利用の依頼DM自動送信の結果（対象外なら null）を同じトーストに添える
      const dmMsg = autoDmMessage(r.auto_dm);
      toast(dmMsg ? `${advanced}。${dmMsg}` : advanced);
      setModal(null);
      if (to >= 8) nav.closePanel();
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const doContacted = async (okMsg: string) => {
    setBusy(true);
    try {
      await postGfContacted(dealId, {});
      await changed();
      toast(okMsg);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doShip = () => {
    if (!carrier.trim() || !trackingNo.trim()) {
      toast("配送業者と追跡番号を入力してください");
      const el = document.getElementById(TRACKING_CARRIER_ID);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
      return;
    }
    void doAdvance({ tracking: { carrier: carrier.trim(), tracking_no: trackingNo.trim() } });
  };

  const onMain = () => {
    if (archived || mainDisabled) return;
    switch (entry.stage) {
      case 1:
        return void doContacted("打診を記録しました（返信待ちへ）");
      case 2:
        return action ? void doContacted("催促を記録しました") : void doAdvance();
      case 3:
        if (d?.token_revoked === 1) return toast("フォームURLが無効です。「再発行」してから進めてください");
        return setModal("adv3");
      case 4:
        if (!received) return void doContacted("催促を記録しました");
        if (GF_PHASE < 2) return doShip();
        if (entry.ship_validation && !entry.ship_validation.ok) {
          // 住所・SKU の不備は発送タブの代理入力（住所）か案件設定（SKU）で直す
          toast(`出荷内容を直してください: ${shipErrorText(entry.ship_validation.errors)}`);
          return nav.set({ tab: "ship", sel: dealId, creator: null });
        }
        return setModal("ship");
      case 5:
        return entry.post_count >= 1 ? void doAdvance() : scrollToPosts("投稿URLを追加してください");
      case 6:
        if (entry.verified_post_count < 1) return scrollToPosts("先に投稿の「確認」を押してください");
        return setModal("adv6");
      case 7:
        if (entry.reuse_allowed_count < 1) return scrollToPosts("2次利用の許諾を記録してください");
        return setModal("adv7");
    }
  };

  const confirmCopyAndAdvance = async (copy: boolean) => {
    if (copy && d?.form_url && d.token_revoked !== 1) {
      toast((await copyText(d.form_url)) ? "フォームURLをコピーしました" : "フォームURLをコピーできませんでした");
    }
    await doAdvance();
  };

  const runStage = async (note: string) => {
    setBusy(true);
    try {
      await postGfSetStage(dealId, { stage: targetStage, note });
      await changed();
      toast(`段階を「${STAGE_NAMES[targetStage]}」に修正しました`);
      setModal(null);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const runShipInstruction = async () => {
    setBusy(true);
    try {
      const r = await postGfShipInstruction(dealId);
      await changed();
      const no = r.entry.ship_order_no ?? "";
      toast(
        r.send_status === "recovered"
          ? `既に出荷指示済みでした（${no}）。受注番号を取り込みました`
          : r.send_status === "dryrun"
            ? `出荷指示を記録しました（${no}・倉庫への送信はOFF＝テスト記録）`
            : `倉庫へ出荷指示を送りました（${no}）。出荷後に追跡番号が自動で入ります`
      );
      setModal(null);
    } catch (e) {
      toast(`出荷指示を送れませんでした: ${errMsg(e)}`);
      await changed();
    } finally {
      setBusy(false);
    }
  };

  const runCancel = async (note: string) => {
    setBusy(true);
    try {
      await postGfCancel(dealId, { note });
      await changed();
      toast("中止にしました（アーカイブへ移動）");
      setModal(null);
      nav.closePanel();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const showForm = !!d && entry.stage >= 3 && entry.stage <= 7 && !cancelled;
  const showTracking = !!d && (received || entry.stage >= 5);
  const showPosts = !!d && (entry.stage >= 5 || entry.post_count > 0);
  const showLinks = !!d && entry.stage >= 3;

  const menu: Array<{ label: string; danger?: boolean; run: () => void }> = [];
  if (!archived && entry.stage === 2 && action) menu.push({ label: "返信あり・条件を確定へ進める", run: () => void doAdvance() });
  if (!archived && ((entry.stage === 2 && !action) || (entry.stage === 4 && !received && !action)))
    menu.push({ label: "催促した（記録）", run: () => void doContacted("催促を記録しました") });
  menu.push({
    label: "段階を戻す／修正",
    run: () => {
      setTargetStage(Math.max(1, cancelled ? entry.stage : archived ? 7 : entry.stage - 1) as GfStage);
      setModal("stage");
    },
  });
  if (!cancelled) menu.push({ label: "中止にする", danger: true, run: () => setModal("cancel") });

  return (
    <>
      <GfPanelFrame onClose={onClose} label="エントリー詳細">
        <div className="gf-pe-head">
          <GfAvatar name={name} size={44} />
          <div className="gf-pe-head-text">
            <span className="gf-pe-name">
              {name}
              {cancelled && <GfTag tone="warn">中止</GfTag>}
            </span>
            <span className="gf-pe-sub">
              {[handleOf(creator ? primarySns(creator.sns) : null), creator?.genre].filter(Boolean).join("・")}
            </span>
          </div>
        </div>

        {sns.length > 0 && (
          <div className="gf-pe-media">
            {sns.map((s) => {
              const [k, ...v] = mediaChip(s.platform, s.follower_count).split(" ");
              return (
                <span key={s.id} className="gf-pe-media-chip">
                  <span className="gf-mono">{k}</span>
                  {v.join(" ")}
                </span>
              );
            })}
          </div>
        )}

        <div className="gf-boxed gf-pe-terms">
          <div className="gf-pe-term">
            <span className="gf-pe-term-k">案件</span>
            <span className="gf-pe-term-v is-strong">{campaign?.name ?? entry.product_name}</span>
          </div>
          <div className="gf-pe-term">
            <span className="gf-pe-term-k">タイプ</span>
            <span>{typeText}</span>
          </div>
          <div className="gf-pe-term">
            <span className="gf-pe-term-k">固定報酬</span>
            <span className="gf-mono">{feeText}</span>
          </div>
          <div className="gf-pe-term">
            <span className="gf-pe-term-k">アフィリ料率</span>
            <span>
              <span className="gf-mono">{rateText}</span>
              {rateSrc && <span className="gf-pe-term-src">{rateSrc}</span>}
            </span>
          </div>
        </div>

        <EntryTimeline stage={entry.stage} events={d?.events ?? null} />

        <div className="gf-pe-factbox">
          <GfFacts rows={factRows} />
          {showAddr && d && entry.address_state === "received" && (
            <div className="gf-boxed gf-addr">
              <div>{d.ship_name}</div>
              <div className="gf-mono">{fmtPostal(d.ship_postal)}</div>
              <div>{d.ship_address}</div>
              <div className="gf-mono">{d.ship_phone}</div>
              {d.address_received_via && <div className="gf-note">受領元: {d.address_received_via}</div>}
            </div>
          )}
        </div>

        {detail.status === "loading" && (
          <div className="gf-pe-loading">
            <GfSkeleton height={18} width="40%" />
            <GfSkeleton height={72} />
          </div>
        )}
        {detail.status === "error" && (
          <GfErrorBanner message={`詳細を読み込めませんでした。${detail.message}`} onRetry={() => void loadDetail()} />
        )}

        {d && (
          <>
            {showForm && <FormUrlBox detail={d} onChanged={changed} toast={toast} />}
            {showTracking && (
              <TrackingForm
                entry={entry}
                carrier={carrier}
                trackingNo={trackingNo}
                onCarrier={setCarrier}
                onTrackingNo={setTrackingNo}
                onShip={doShip}
                onSaved={changed}
                toast={toast}
                busy={busy}
              />
            )}
            {showPosts && <PostsSection detail={d} nowIso={nowIso} onChanged={changed} toast={toast} focusSignal={postsSignal} />}
            {showLinks && <LinksSection detail={d} onChanged={changed} toast={toast} />}
            {paid && <PaymentSection detail={d} onChanged={changed} toast={toast} />}
            <NotesSection detail={d} onChanged={changed} toast={toast} />
          </>
        )}

        <div className="gf-panel-foot gf-pe-foot">
          <GfButton variant="primary" size="lg" disabled={busy || mainDisabled} onClick={onMain}>
            {mainLabel}
          </GfButton>
          <GfButton size="lg" onClick={() => setModal("message")} disabled={!d}>
            {linkedIgOf(d?.sns ?? creator?.sns ?? []) ? "DM" : "メッセージ"}
          </GfButton>
          <div className="gf-pe-more">
            <GfButton size="lg" onClick={() => setMenuOpen((v) => !v)} title="その他の操作">
              ⋯
            </GfButton>
            {menuOpen && (
              <div className="gf-pe-menu" role="menu">
                {menu.map((m) => (
                  <button
                    key={m.label}
                    type="button"
                    role="menuitem"
                    className={`gf-pe-menu-item${m.danger ? " is-danger" : ""}`}
                    onClick={() => {
                      setMenuOpen(false);
                      m.run();
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </GfPanelFrame>

      <AdvanceConfirm
        open={modal === "adv3"}
        title="住所フォームを送る段階へ進めます"
        message="条件合意 → 住所回収へ進めます。クリエイターに本人入力フォームのURLを渡してください。"
        checkLabel="フォームURLをクリップボードにコピーする"
        defaultChecked
        confirmLabel="進める"
        busy={busy}
        onConfirm={(c) => void confirmCopyAndAdvance(c)}
        onClose={() => setModal(null)}
      />
      <AdvanceConfirm
        open={modal === "adv6"}
        title={campaign?.reuse_required === 0 ? "投稿を確認してアーカイブします" : "投稿を確認して2次利用許諾へ進めます"}
        message="投稿の内容を確認しました。景品表示法（ステマ規制）のため、PR／タイアップ表記の有無を必ず確かめてください。"
        checkLabel="投稿に PR／タイアップ表記があることを確認した"
        requireCheck
        confirmLabel={campaign?.reuse_required === 0 ? "確認してアーカイブ" : "確認して進める"}
        busy={busy}
        onConfirm={(c) => void doAdvance({ pr_label_checked: c })}
        onClose={() => setModal(null)}
      >
        {willAutoSendReuseDm(campaign) && (
          <div className="gf-banner is-info gf-adv-auto-dm">
            2次利用の依頼DMを自動で送ります（案件の設定）。Instagram と連携済みで、相手の最後のメッセージから24時間以内のときだけ届きます。送れなかったときは履歴に残るので、手動で送ってください。
          </div>
        )}
      </AdvanceConfirm>
      <AdvanceConfirm
        open={modal === "adv7"}
        title="2次利用の許諾を記録してアーカイブします"
        message="許諾済みの投稿を確認しました。アーカイブするとカンバンから外れ、「アーカイブ」ビューに移動します。"
        confirmLabel="アーカイブする"
        busy={busy}
        onConfirm={() => void doAdvance()}
        onClose={() => setModal(null)}
      />
      <ConfirmModal
        open={modal === "ship"}
        title="倉庫へ出荷指示を送ります"
        message="0円のギフト出荷として倉庫（ロジクム）へ出荷指示を送ります。送信後の取り消しは倉庫への連絡が必要です。宛先と商品を確認してください。"
        confirmLabel={busy ? "送信中…" : "出荷指示を送る"}
        busy={busy}
        onConfirm={() => void runShipInstruction()}
        onClose={() => !busy && setModal(null)}
      >
        <div className="gf-boxed gf-ship-confirm">
          {[
            ["宛名", d?.ship_name ?? "—"],
            ["郵便番号", fmtPostal(d?.ship_postal ?? null) || "—"],
            ["住所", d?.ship_address ?? "—"],
            ["電話", d?.ship_phone ?? "—"],
            ["商品", `${entryProductLabel(entry, campaign)} × ${entry.quantity}`],
            ["SKU", shipSku ?? "未設定"],
          ].map(([k, v]) => (
            <div className="gf-ship-confirm-row" key={k}>
              <span className="gf-ship-confirm-k">{k}</span>
              <span className="gf-ship-confirm-v">{v}</span>
            </div>
          ))}
        </div>
        {!shipSku && (
          <div className="gf-banner is-error">
            {entry.product_pending
              ? "クリエイターがまだセットを選んでいません。本人フォームで選んでもらうか、発送タブの代理入力で選んでください。"
              : "SKU が未設定のため送れません。案件の設定で SKU を入れてください。"}
          </div>
        )}
      </ConfirmModal>
      <ConfirmModal
        open={modal === "cancel"}
        title="このエントリーを中止にします"
        message="中止にするとフォームURLは無効になり、アーカイブへ移動します。"
        confirmLabel="中止にする"
        danger
        requireNote
        noteLabel="中止の理由"
        busy={busy}
        onConfirm={(note) => void runCancel(note)}
        onClose={() => setModal(null)}
      />
      <ConfirmModal
        open={modal === "stage"}
        title="段階を戻す／修正する"
        message="誤操作の取り消しなど、段階を直接変更します。履歴に理由が残ります。"
        confirmLabel="段階を変更する"
        requireNote
        busy={busy}
        onConfirm={(note) => void runStage(note)}
        onClose={() => setModal(null)}
      >
        <GfField label="変更後の段階">
          <select className="gf-select" value={targetStage} onChange={(e) => setTargetStage(Number(e.target.value) as GfStage)}>
            {([1, 2, 3, 4, 5, 6, 7, 8] as GfStage[]).map((s) => (
              <option key={s} value={s}>
                {s} {STAGE_NAMES[s]}
                {s === entry.stage ? "（現在）" : ""}
              </option>
            ))}
          </select>
        </GfField>
      </ConfirmModal>
      <DmPanel
        open={modal === "message"}
        ctx={{
          name,
          product: entryProductLabel(entry, campaign),
          formUrl: d?.form_url ?? null,
          tokenRevoked: d?.token_revoked === 1,
          deadline: entry.post_deadline ? fmtYmd(entry.post_deadline) || entry.post_deadline : null,
        }}
        sns={d?.sns ?? creator?.sns ?? []}
        dealId={dealId}
        influencerId={entry.influencer_id}
        closed={archived}
        onClose={() => setModal(null)}
        onSent={changed}
        onOpenCreator={() => {
          setModal(null);
          nav.openCreator(entry.influencer_id);
        }}
        toast={toast}
      />
    </>
  );
}
